import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import CountryPicker from "../components/CountryPicker";

const USERNAME_RE = /^[a-zA-Z0-9_]{3,20}$/;

export default function FinishRegistration() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [country, setCountry] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (!user.needs_onboarding) {
      navigate("/dashboard", { replace: true });
      return;
    }
    setFirstName(user.first_name || "");
    setLastName(user.last_name || "");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!USERNAME_RE.test(username)) { setErr("Username must be 3–20 chars; letters, numbers, underscore."); return; }
    if (!country) { setErr("Country is required."); return; }
    setBusy(true);
    try {
      await api.post("/auth/complete-onboarding", {
        username, country,
        first_name: firstName || null,
        last_name: lastName || null,
      });
      await refreshMe();
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not finish onboarding");
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[var(--bg)] text-zinc-200 flex items-center justify-center text-xs uppercase tracking-[0.4em]">
        loading…
      </div>
    );
  }

  return (
    <div data-testid="finish-registration-page" className="min-h-screen bg-[var(--bg)] text-zinc-100 flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">step 2 of 2</div>
        <h1 className="text-4xl font-bold mt-2">One more step.</h1>
        <p className="text-sm text-zinc-400 mt-2">Pick your username and country to get started. Username is permanent.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Username</label>
            <input
              data-testid="finish-username"
              type="text" required minLength={3} maxLength={20}
              value={username} onChange={(e) => setUsername(e.target.value.replace(/\s+/g, ""))}
              placeholder="your_handle"
              className="mt-1 w-full px-4 py-3 rounded-lg bg-zinc-900 border border-white/10 focus:border-[var(--accent)] outline-none transition"
            />
            <div className="text-[11px] text-zinc-500 mt-1">3–20 chars · letters, numbers, underscore</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">First name</label>
              <input
                data-testid="finish-first-name"
                type="text" maxLength={50}
                value={firstName} onChange={(e) => setFirstName(e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-lg bg-zinc-900 border border-white/10 focus:border-[var(--accent)] outline-none transition"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Last name</label>
              <input
                data-testid="finish-last-name"
                type="text" maxLength={50}
                value={lastName} onChange={(e) => setLastName(e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-lg bg-zinc-900 border border-white/10 focus:border-[var(--accent)] outline-none transition"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Country</label>
            <CountryPicker
              value={country}
              onChange={setCountry}
              required
              testId="finish-country"
            />
          </div>

          {err && (
            <div data-testid="finish-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {err}
            </div>
          )}

          <button type="submit" disabled={busy} data-testid="finish-submit"
            className="w-full px-5 py-3 rounded-full bg-[var(--accent)] text-black font-bold hover:brightness-110 disabled:opacity-50 transition">
            {busy ? "saving…" : "Finish setup"}
          </button>
        </form>
      </div>
    </div>
  );
}
